import { useQuery, useMutation } from '@tanstack/react-query';
import { useAPI } from '@pella/cosmos-react';
import { QUERY_KEYS } from '../../utils/enums';
import { normalizeAnalyticsRows } from '../../utils/analytics';

const createUserAnalyticsMutation = `
  mutation createUserAnalytics($input: CreateUserAnalyticsInput!) {
    createUserAnalytics(input: $input) {
      id
      success
    }
  }
`;

const userAnalyticsRowsQuery = `
  query userAnalyticsRows($limit: Int = 500, $offset: Int = 0, $filters: UserAnalyticsRowsFilters) {
    userAnalyticsRows(limit: $limit, offset: $offset, filters: $filters) {
      id
      lastEntryAt
      userId
      userEmail
      empNumber
      equipmentNumber
      siteId
      serviceArea
      serviceAction
      comment
      outcome
      arrivedAt
      questionAnswers {
        question
        answer
        additionalDetails
        answeredAt
      }
      cancellationReason
      cancellationReasonDetails
      qaEngagement
    }
  }
`;

const userAnalyticsRowsCountQuery = `
  query userAnalyticsRowsCount($filters: UserAnalyticsRowsFilters) {
    userAnalyticsRowsCount(filters: $filters)
  }
`;

const userAnalyticsFilterOptionsQuery = `
  query userAnalyticsFilterOptions($filters: UserAnalyticsRowsFilters) {
    userAnalyticsFilterOptions(filters: $filters) {
      siteIds
      serviceAreas
      serviceActions
      outcomes
      cancellationReasons
    }
  }
`;

const userAnalyticsEngagementSummaryQuery = `
  query userAnalyticsEngagementSummary($filters: UserAnalyticsRowsFilters) {
    userAnalyticsEngagementSummary(filters: $filters) {
      sessionsStarted
      reachedOutcome
      submittedCount
      cancelledCount
      submittedWithQaCount
      highQualitySubmittedCount
      qaAssessedCount
      qaSpentTimeCount
      dailyTrend {
        day
        sessionsCount
        highQualityCount
      }
    }
  }
`;

const userAnalyticsExportQuery = `
  query userAnalyticsExport($filters: UserAnalyticsRowsFilters) {
    userAnalyticsExport(filters: $filters) {
      filename
      csv
    }
  }
`;

const sanitizeString = (value) => {
  if (typeof value !== 'string') return undefined;

  const trimmed = value.trim();
  return trimmed || undefined;
};

const sanitizeAnalyticsFilters = (filters = {}) => {
  const normalizedFilters = {
    siteId: sanitizeString(filters.siteId),
    serviceArea: sanitizeString(filters.serviceArea),
    serviceAction: sanitizeString(filters.serviceAction),
    outcome: sanitizeString(filters.outcome),
    cancellationReason: sanitizeString(filters.cancellationReason),
    userId: sanitizeString(filters.userId),
    userEmail: sanitizeString(filters.userEmail),
    empNumber: sanitizeString(filters.empNumber),
    equipmentNumber: sanitizeString(filters.equipmentNumber),
    fromLastEntryAt: sanitizeString(filters.fromLastEntryAt),
    toLastEntryAt: sanitizeString(filters.toLastEntryAt),
    search: sanitizeString(filters.search),
  };

  const activeFilterEntries = Object.entries(normalizedFilters).filter(([, value]) => value !== undefined);
  return activeFilterEntries[0] ? Object.fromEntries(activeFilterEntries) : undefined;
};

/**
 * Fetches follow-up questions for a maintenance conversation.
 * Fires automatically when conversationId is present and apiReady is true.
 * @param {{ conversationId?: string, userLanguage?: string }} input
 * @param {object} [options]
 */
export const useMaintenanceFollowUpQuestions = ({ conversationId, userLanguage } = {}, options = {}) => {
  const { api, apiReady } = useAPI();
  return useQuery({
    queryKey: [QUERY_KEYS.MAINTENANCE_FOLLOW_UP_QUESTIONS, conversationId, userLanguage],
    queryFn: () =>
      api.maintenanceCopilot.getMaintenanceFollowUpQuestions({
        conversationId: /* istanbul ignore next -- query disabled when conversationId is falsy */ conversationId ?? '',
        userLanguage,
      }),
    enabled: apiReady && Boolean(conversationId),
    staleTime: 0,
    ...options,
  });
};

/**
 * Generates a work order summary. Fire imperatively via `mutate(input)`.
 * @param {object} [options]
 * @returns {import('@tanstack/react-query').UseMutationResult<unknown, Error, unknown>}
 */
export const useWorkOrderSummary = (options = {}) => {
  const { api } = useAPI();
  const mutationFn = (input) => api.maintenanceCopilot.getWorkOrderSummary(input);
  return /** @type {import('@tanstack/react-query').UseMutationResult<unknown, Error, unknown>} */ (
    useMutation({ ...options, mutationFn })
  );
};

/**
 * Creates a maintenance copilot conversation. Fire imperatively via `mutate(input)`.
 * @param {object} [options]
 * @returns {import('@tanstack/react-query').UseMutationResult<unknown, Error, unknown>}
 */
export const useCreateConversation = (options = {}) => {
  const { api } = useAPI();
  const mutationFn = (input) => api.maintenanceCopilot.createConversation(input);
  return /** @type {import('@tanstack/react-query').UseMutationResult<unknown, Error, unknown>} */ (
    useMutation({ ...options, mutationFn })
  );
};

/**
 * Creates a work order tracking entry. Fire imperatively via `mutate(input)`.
 * @param {object} [options]
 * @returns {import('@tanstack/react-query').UseMutationResult<unknown, Error, unknown>}
 */
export const useCreateWorkOrderTracking = (options = {}) => {
  const { api } = useAPI();
  const mutationFn = (input) => api.maintenanceCopilot.createWorkOrderTracking(input);
  return /** @type {import('@tanstack/react-query').UseMutationResult<unknown, Error, unknown>} */ (
    useMutation({ ...options, mutationFn })
  );
};

/**
 * Creates or updates a user analytics row. Fire imperatively via `mutate(input)`.
 * @param {object} [options]
 * @returns {import('@tanstack/react-query').UseMutationResult<unknown, Error, unknown>}
 */
export const useCreateUserAnalytics = (options = {}) => {
  const { api } = useAPI();
  const mutationFn = (input) => api.maintenanceCopilot._graphqlRequest(createUserAnalyticsMutation, { input });
  return /** @type {import('@tanstack/react-query').UseMutationResult<unknown, Error, unknown>} */ (
    useMutation({ ...options, mutationFn })
  );
};

/**
 * Fetches user analytics rows for the analytics table.
 * @param {{ limit?: number, offset?: number, filters?: object }} [input]
 * @param {object} [options]
 */
export const useUserAnalyticsRows = ({ limit = 500, offset = 0, filters = {} } = {}, options = {}) => {
  const { api } = useAPI();
  const safeFilters = sanitizeAnalyticsFilters(filters);

  return useQuery({
    queryKey: [QUERY_KEYS.USER_ANALYTICS_ROWS, limit, offset, safeFilters],
    queryFn: () => {
      const requestedLimit = Number.isFinite(limit) ? Math.trunc(limit) : 500;
      const requestedOffset = Number.isFinite(offset) ? Math.trunc(offset) : 0;
      const safeLimit = Math.max(requestedLimit, 1);
      const safeOffset = Math.min(Math.max(requestedOffset, 0), 1_000_000);

      return api.maintenanceCopilot
        ._graphqlRequest(userAnalyticsRowsQuery, {
          limit: safeLimit,
          offset: safeOffset,
          filters: safeFilters,
        })
        .then((rows) => normalizeAnalyticsRows(rows).slice(0, safeLimit));
    },
    enabled: true,
    staleTime: 0,
    ...options,
  });
};

/**
 * Fetches total analytics row count for the active filters.
 * @param {{ filters?: object }} [input]
 * @param {object} [options]
 */
export const useUserAnalyticsRowsCount = ({ filters = {} } = {}, options = {}) => {
  const { api } = useAPI();
  const safeFilters = sanitizeAnalyticsFilters(filters);

  return useQuery({
    queryKey: [QUERY_KEYS.USER_ANALYTICS_ROWS_COUNT, safeFilters],
    queryFn: () =>
      api.maintenanceCopilot._graphqlRequest(userAnalyticsRowsCountQuery, { filters: safeFilters }).then((count) => {
        if (Number.isFinite(count)) return Math.max(0, Math.trunc(count));

        const wrappedCount =
          count && typeof count === 'object' ? /** @type {{ userAnalyticsRowsCount?: unknown }} */ (count) : null;
        const candidate = Number(wrappedCount?.userAnalyticsRowsCount ?? 0);
        return Number.isFinite(candidate) ? Math.max(0, Math.trunc(candidate)) : 0;
      }),
    enabled: true,
    staleTime: 0,
    ...options,
  });
};

/**
 * Fetches backend-provided option values for analytics filters.
 * @param {{ filters?: object }} [input]
 * @param {object} [options]
 */
export const useUserAnalyticsFilterOptions = ({ filters = {} } = {}, options = {}) => {
  const { api } = useAPI();
  const safeFilters = sanitizeAnalyticsFilters(filters);

  return useQuery({
    queryKey: [QUERY_KEYS.USER_ANALYTICS_FILTER_OPTIONS, safeFilters],
    queryFn: () =>
      api.maintenanceCopilot._graphqlRequest(userAnalyticsFilterOptionsQuery, { filters: safeFilters }).then((data) => {
        const fallback = {
          siteIds: [],
          serviceAreas: [],
          serviceActions: [],
          outcomes: [],
          cancellationReasons: [],
        };

        const response =
          data && typeof data === 'object' ? /** @type {{ userAnalyticsFilterOptions?: object }} */ (data) : null;
        const optionsPayload = response?.userAnalyticsFilterOptions || data;

        if (!optionsPayload || typeof optionsPayload !== 'object') return fallback;

        return {
          siteIds: Array.isArray(optionsPayload.siteIds) ? optionsPayload.siteIds : [],
          serviceAreas: Array.isArray(optionsPayload.serviceAreas) ? optionsPayload.serviceAreas : [],
          serviceActions: Array.isArray(optionsPayload.serviceActions) ? optionsPayload.serviceActions : [],
          outcomes: Array.isArray(optionsPayload.outcomes) ? optionsPayload.outcomes : [],
          cancellationReasons: Array.isArray(optionsPayload.cancellationReasons)
            ? optionsPayload.cancellationReasons
            : [],
        };
      }),
    enabled: true,
    staleTime: 0,
    ...options,
  });
};

const EMPTY_ENGAGEMENT_SUMMARY = {
  sessionsStarted: 0,
  reachedOutcome: 0,
  submittedCount: 0,
  cancelledCount: 0,
  submittedWithQaCount: 0,
  highQualitySubmittedCount: 0,
  qaAssessedCount: 0,
  qaSpentTimeCount: 0,
  dailyTrend: [],
};

/**
 * Fetches backend-computed funnel and daily trend statistics for analytics filters.
 * @param {{ filters?: object }} [input]
 * @param {object} [options]
 */
export const useUserAnalyticsEngagementSummary = ({ filters = {} } = {}, options = {}) => {
  const { api } = useAPI();
  const safeFilters = sanitizeAnalyticsFilters(filters);

  return useQuery({
    queryKey: [QUERY_KEYS.USER_ANALYTICS_ENGAGEMENT_SUMMARY, safeFilters],
    queryFn: () =>
      api.maintenanceCopilot
        ._graphqlRequest(userAnalyticsEngagementSummaryQuery, { filters: safeFilters })
        .then((data) => {
          const summary =
            data && typeof data === 'object' ? /** @type {{ userAnalyticsEngagementSummary?: object }} */ (data) : null;
          const payload = summary?.userAnalyticsEngagementSummary || data;

          if (!payload || typeof payload !== 'object') return EMPTY_ENGAGEMENT_SUMMARY;

          const typedPayload = /** @type {typeof EMPTY_ENGAGEMENT_SUMMARY} */ (payload);

          return {
            sessionsStarted: typedPayload.sessionsStarted ?? 0,
            reachedOutcome: typedPayload.reachedOutcome ?? 0,
            submittedCount: typedPayload.submittedCount ?? 0,
            cancelledCount: typedPayload.cancelledCount ?? 0,
            submittedWithQaCount: typedPayload.submittedWithQaCount ?? 0,
            highQualitySubmittedCount: typedPayload.highQualitySubmittedCount ?? 0,
            qaAssessedCount: typedPayload.qaAssessedCount ?? 0,
            qaSpentTimeCount: typedPayload.qaSpentTimeCount ?? 0,
            dailyTrend: Array.isArray(typedPayload.dailyTrend) ? typedPayload.dailyTrend : [],
          };
        }),
    enabled: true,
    staleTime: 0,
    ...options,
  });
};

/**
 * Exports user analytics rows matching the given filters as CSV.
 * Fire imperatively via `mutateAsync(filters)`.
 * @param {object} [options]
 * @returns {import('@tanstack/react-query').UseMutationResult<{ filename: string, csv: string }, Error, object>}
 */
export const useExportUserAnalyticsRows = (options = {}) => {
  const { api } = useAPI();
  const mutationFn = (filters) => {
    const safeFilters = sanitizeAnalyticsFilters(filters);

    return api.maintenanceCopilot._graphqlRequest(userAnalyticsExportQuery, { filters: safeFilters }).then((data) => {
      const response = data && typeof data === 'object' ? /** @type {{ userAnalyticsExport?: object }} */ (data) : null;
      const payload = response?.userAnalyticsExport || data;

      return {
        filename: typeof payload?.filename === 'string' ? payload.filename : 'user-analytics-export.csv',
        csv: typeof payload?.csv === 'string' ? payload.csv : '',
      };
    });
  };

  return /** @type {import('@tanstack/react-query').UseMutationResult<{ filename: string, csv: string }, Error, object>} */ (
    useMutation({ ...options, mutationFn })
  );
};

/**
 * Translates an array of text inputs. Fire imperatively via `mutateAsync(input)`.
 * @param {object} [options]
 * @returns {import('@tanstack/react-query').UseMutationResult<unknown, Error, unknown>}
 */
export const useGetTranslation = (options = {}) => {
  const { api } = useAPI();
  const mutationFn = (input) => api.maintenanceCopilot.getTranslation(input);
  return /** @type {import('@tanstack/react-query').UseMutationResult<unknown, Error, unknown>} */ (
    useMutation({ ...options, mutationFn })
  );
};
